import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { BodyOf } from "../../../../../core/api";
import useApi from "../../../../../shared/hooks/useApi";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import { createCaregiverStaffingPreferencesDraft } from "./staffingPreferences.utils";

type CaregiverStaffingPreferencesBody = BodyOf<
  "put",
  "./caregivers/:caregiverId/staffing_preferences"
>;

export function useCaregiverProfileStaffingPreferencesController(caregiverId: CaregiverId) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const [draft, setDraft] = React.useState<CaregiverStaffingPreferencesBody | null>(null);
  const queryKey = ["caregiverProfileModal", caregiverId, "staffingPreferences"];

  const staffingPreferencesQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.get("./caregivers/:caregiverId/staffing_preferences", {
        path: { caregiverId },
      }),
  });

  const updateStaffingPreferencesMutation = useMutation({
    mutationFn: (body: CaregiverStaffingPreferencesBody) =>
      api.put("./caregivers/:caregiverId/staffing_preferences", {
        path: { caregiverId },
        body,
      }),
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKey, preferences);
      toast({
        description: "Staffing preferences updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update staffing preferences.",
        status: "error",
      });
    },
  });

  const preferences = staffingPreferencesQuery.data;

  const commitDraft = (nextDraft: CaregiverStaffingPreferencesBody) => {
    setDraft(nextDraft);
    updateStaffingPreferencesMutation.mutate(nextDraft);
  };

  const toggleEditMode = () => {
    setDraft((currentDraft) => {
      if (currentDraft !== null) {
        return null;
      }

      if (preferences === undefined) {
        return currentDraft;
      }

      return createCaregiverStaffingPreferencesDraft(preferences);
    });
  };

  return {
    commitDraft,
    draft,
    isEditing: draft !== null,
    isUpdating: updateStaffingPreferencesMutation.isPending,
    preferences,
    refetchPreferences: staffingPreferencesQuery.refetch,
    setDraft,
    staffingPreferencesQuery,
    toggleEditMode,
  };
}
